/* eslint-disable tailwindcss/no-arbitrary-value */
import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { extend } from '@thd-nucleus/data-sources';
import {
  RadioGroup, Radio, Button,
  SelectionControlLabel,
  DrawerFooter, DrawerBody, Typography
} from '@one-thd/sui-atomic-components';
import {
  useProjectDetails,
  projectDetailsModel,
  useCreateGroup,
  createGroupModel
} from '../../hooks/useProjects';
import { AddToContext } from '../../AddToContext';
import { SCREENS, GROUP_ALREADY_EXITS_ERROR } from '../../AddToConstants';
import { AddToProductDetails } from '../../common/AddToProductDetails/AddToProductDetails';
import { ProjectsGroupSelection } from './ProjectsGroupSelection';

export const SelectGroup = ({
  projectId,
  handleClose,
  products,
  addItem,
  onCreateGroup
}) => {
  const {
    setScreen,
    selectedGroup,
    setSelectedGroup,
    setShowSuccessMessage
  } = useContext(AddToContext);
  const { DEFAULT } = SCREENS;
  const [newGroupName, setNewGroupName] = useState();
  const [groupErrorMessaging, setGroupErrorMessaging] = useState('');
  const [checkGroup, setCheckGroup] = useState(false);
  const { projectData, projectRefetch } = useProjectDetails({ projectId });
  const {
    itemGroups,
    defaultListId,
    defaultListMutationKey
  } = projectData?.projectDetails || {};

  const getGroupName = (groupName, errorMessaging) => {
    setNewGroupName(groupName);
    setGroupErrorMessaging(errorMessaging);
  };

  const { createGroup, createGroupResponse } = useCreateGroup({
    projectId,
    listId: defaultListId,
    mutationKey: defaultListMutationKey,
    groupName: newGroupName
  });

  const groups = (itemGroups || []).filter((itemGroup) => itemGroup.groupName !== '' && itemGroup.groupName !== 'Unassigned');

  const onSelectGroup = (event) => {
    checkGroup && setCheckGroup(false);
    const groupName = event.target.value;
    // Receiving the radio element component as value [object Object] converted to string
    if (groupName.indexOf('object') === -1) {
      setSelectedGroup(groupName);
    }
  };

  const addItemToProject = async () => {
    await addItem({ type: 'OLD_GROUP' });
  };

  const radioCheck = (event) => {
    if (event.target.checked) {
      setCheckGroup(true);
    }
  };

  const handleSubmit = async (submitEvent) => {
    submitEvent.preventDefault();
    if (!groupErrorMessaging) {
      if (!getGroupName) {
        setGroupErrorMessaging('Please enter a group name');
        setGroupErrorMessaging(GROUP_ALREADY_EXITS_ERROR);
      } else {
        await createGroup();
      }
    }
  };

  useEffect(() => {
    if (createGroupResponse?.data?.createProjectListGroup?.groupId) {
      if (!onCreateGroup) {
        LIFE_CYCLE_EVENT_BUS.trigger('projects: group modification', {
          groupAction: 'group created'
        });
      }
      setShowSuccessMessage(true);
      setScreen(DEFAULT);
      projectRefetch();
      onClose(true);
    }
    if (createGroupResponse?.error) {
      if (createGroupResponse?.error?.message.includes(GROUP_ALREADY_EXITS_ERROR)) {
        setGroupErrorMessaging(GROUP_ALREADY_EXITS_ERROR);
      }
    }

  }, [createGroupResponse?.data, createGroupResponse?.error]);

  const onClose = (isCreated = false) => {
    setNewGroupName('');
    setGroupErrorMessaging('');
    if (isCreated) {
      projectRefetch();
      if (onCreateGroup) {
        onCreateGroup(newGroupName);
      } else {
        handleClose();
      }
    } else {
      handleClose();
    }
  };

  const onUnassignedGroupClick = async () => {
    setSelectedGroup('Unassigned');
    // Calling the mutation directly with unassigned as the state does not update to unassigned
    // when the mutation is executed, Then we could get a null group in the mutation
    await addItem({ type: 'OLD_GROUP', groupName: 'Unassigned' });
  };

  return (
    <>
      <DrawerBody overflow="scroll">
        {products?.length <= 1 && (
          <AddToProductDetails products={products} />
        )}
        <div className="sui-flex sui-flex-col sui-gap-1 sui-pt-3">
          <Button
            name="Unassigned Group"
            fullWidth
            variant={!selectedGroup ? 'primary' : 'secondary'}
            onClick={onUnassignedGroupClick}
            aria-label="add-to-project-unassigned-group"
            data-testid="add-to-project-unassigned-group"
          >
            Add to Project
          </Button>
        </div>
        <div className="sui-pl-5">
          <div className="sui-px-8 sui-py-3">
            <Typography weight="bold">
              -or-
            </Typography>
          </div>
          <Typography color="primary" variant="h4">
            Add to a Group
          </Typography>

          <RadioGroup
            aria-labelledby="group-selection-radio-buttons-group"
            name="radio-buttons-group"
            onChange={onSelectGroup}
          >
            <SelectionControlLabel
              value={(
                <ProjectsGroupSelection
                  projectId={projectId}
                  handleClose={handleClose}
                  onCreateGroup={onCreateGroup}
                  getGroupName={getGroupName}
                  checkGroup={checkGroup}
                  groupErrorMessaging={groupErrorMessaging}
                  setShowSuccessMessage={setShowSuccessMessage}
                  setScreen={setScreen}
                  onClose={onClose}
                />
              )}
              label={(
                <ProjectsGroupSelection
                  projectId={projectId}
                  handleClose={handleClose}
                  onCreateGroup={onCreateGroup}
                  getGroupName={getGroupName}
                  checkGroup={checkGroup}
                  groupErrorMessaging={groupErrorMessaging}
                  setShowSuccessMessage={setShowSuccessMessage}
                  setScreen={setScreen}
                  onClose={onClose}
                />
              )}
            >
              <Radio name="radio-1" checked={checkGroup === true} onChange={(event) => radioCheck(event)} />
            </SelectionControlLabel>
            {
              groups?.map((group, i) => (
                <SelectionControlLabel value={group.groupName} label={group.groupName} key={i}>
                  <Radio />
                </SelectionControlLabel>
              ))
            }
          </RadioGroup>
        </div>
      </DrawerBody>
      <DrawerFooter position="center" data-testid="add-to-project-add-to-group-footer">
        <div className="sui-flex sui-flex-col sui-gap-1">
          {checkGroup
            ? (
              <Button
                data-testid="create-group-submit-button"
                variant="primary"
                fullWidth
                onClick={handleSubmit}
                type="submit"
                disabled={newGroupName === null || !checkGroup}
              > Create Group
              </Button>
            )
            : (
              <Button
                data-testid="add-to-project-add-button"
                variant="primary"
                fullWidth
                disabled={!selectedGroup}
                onClick={addItemToProject}
                type="submit"
              > Add
              </Button>
            )}
          <Button
            data-testid="add-to-project-cancel-button"
            variant="secondary"
            fullWidth
            onClick={handleClose}
            type="submit"
          >
            Cancel
          </Button>
        </div>
      </DrawerFooter>
    </>
  );
};

SelectGroup.propTypes = {
  projectId: PropTypes.string.isRequired,
  handleClose: PropTypes.func.isRequired,
  addItem: PropTypes.func.isRequired,
  products: PropTypes.arrayOf(PropTypes.shape({
    itemId: PropTypes.string,
    quantity: PropTypes.number
  }))
};

SelectGroup.defaultProps = {
  products: []
};

SelectGroup.dataModel = extend(
  AddToProductDetails,
  projectDetailsModel,
  createGroupModel
);
